import React, { useState, useEffect } from 'react';
import { MediaMessage } from '../../../../../chat-api';
import moment from 'moment-with-locales-es6';
import { isMsgOut } from '../../../../../utils';
import { LittleLoading } from "../../Message";

import {
  AudioContainer,
  MediaRow,
  AudioControls,
  AudioControlsRow,
  AudioPlay,
  AudioPlayButton,
  AudioProgressContainer,
  AudioDuration,
  AudioProgress,
  AudioProgressSpan,
  AudioProgressInput
} from './styles';
import { useChatState } from '../../../../../data';

interface MessageBodyAudioProps {
  message: MediaMessage;
  mediaAudio: HTMLAudioElement | null;
  backgroundColor?: string;
  textColor?: string;
  runnableTrackColor?: string; 
  sliderTrackColor?: string; 
}

function MessageBodyAudio({ message, mediaAudio, textColor, backgroundColor, runnableTrackColor, sliderTrackColor }: MessageBodyAudioProps) {
  const { chat } = useChatState();

  const [audioState, setAudioState] = useState({ playing: false });

  useEffect(() => {
    if (mediaAudio) {
      mediaAudio.ontimeupdate = function () {
        setAudioState(aState => {
          return {
            ...aState,
            playing: mediaAudio.ended ? false : aState.playing,
            currentTime: mediaAudio.currentTime
          }
        });
      }
    }
  }, [mediaAudio]);

  function playAudio() {
    if (mediaAudio) {
      mediaAudio.paused ? mediaAudio.play() : mediaAudio.pause();
      setAudioState({ ...audioState, playing: !audioState.playing })
    }
  }

  function getAudioProgess() {
    return mediaAudio ?
      ((Number(mediaAudio.currentTime) * 100) / Number(mediaAudio.duration)).toFixed(2)
      : 0;
  }

  function getAudioCurrentTime() {
    const today = moment().utcOffset(0);
    if (mediaAudio) {
      const duration = audioState.playing ? mediaAudio.currentTime : mediaAudio.duration;
      today.set({ hour: 0, minute: 0, second: duration, millisecond: 0 });
      if (duration < 600) {
        return today.format('m:ss');
      } else if (duration < 3600) {
        return today.format('mm:ss');
      } else {
        return today.format('hh:mm');
      }
    }
    return '0:00';
  }

  function handleChangeAudioProgress(e: any) {
    if (mediaAudio) {
      mediaAudio.currentTime = (mediaAudio.duration * e.target.value) / 100;
    }
  }

  return (
    <AudioContainer as="sdiv">
      <MediaRow as="sdiv" style={isMsgOut(message, chat?.communicationTool) ? { flexDirection: 'row-reverse' } : {}}>
        <AudioControls as="sdiv">
          <AudioControlsRow as="sdiv">
            <AudioPlay as="sdiv">
              <AudioPlayButton onClick={playAudio} backgroundColor={backgroundColor} textColor={textColor}>
                {!mediaAudio || !mediaAudio.src ?
                  <LittleLoading textColor={textColor} message={message}/>
                : !audioState.playing ?
                  <sdiv data-icon="audio-play" style={{backgroundColor: backgroundColor, display: 'inline'}}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 34 34" width="34" height="34">
                      <path fill="currentColor" d="M8.5 8.7c0-1.7 1.2-2.4 2.6-1.5l14.4 8.3c1.4.8 1.4 2.2 0 3l-14.4 8.3c-1.4.8-2.6.2-2.6-1.5V8.7z"></path>
                    </svg>
                  </sdiv>
                  :
                  <sdiv data-icon="audio-pause" style={{display: 'inline'}}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 34 34" width="34" height="34">
                      <path fill="currentColor" d="M9.2 25c0 .5.4 1 .9 1h3.6c.5 0 .9-.4.9-1V9c0-.5-.4-.9-.9-.9h-3.6c-.4-.1-.9.3-.9.9v16zm11-17c-.5 0-1 .4-1 .9V25c0 .5.4 1 1 1h3.6c.5 0 1-.4 1-1V9c0-.5-.4-.9-1-.9 0-.1-3.6-.1-3.6-.1z"></path>
                    </svg>
                  </sdiv>
                }
              </AudioPlayButton>
            </AudioPlay>
            <AudioProgressContainer as="sdiv">
              <AudioDuration as="sdiv" textColor={textColor!}>{getAudioCurrentTime()}</AudioDuration>
              <AudioProgress as="sdiv">
                <AudioProgressSpan as="sdiv" style={{ backgroundColor: sliderTrackColor , width: `${getAudioProgess()}%` }} />
                <AudioProgressInput
                  dir="ltr"
                  type="range"
                  className={isMsgOut(message, chat?.communicationTool) ? 'inputRangeSliderRunnableOut inputRangeThumbOut' : ''}
                  min="0"
                  max="100"
                  value={String(getAudioProgess())}
                  runnableTrackColor={runnableTrackColor}
                  sliderTrackColor={sliderTrackColor}
                  onChange={handleChangeAudioProgress}
                />
                {!!mediaAudio && <audio src={mediaAudio.src} preload="auto"></audio>}
              </AudioProgress>
            </AudioProgressContainer>
          </AudioControlsRow>
        </AudioControls>
      </MediaRow>
    </AudioContainer>
  );
}

export default React.memo(MessageBodyAudio)